import type { ReviewStatus } from '../types.js';

/**
 * Review-status pill. Mirrors ChannelBadge in shape (dot + label,
 * rounded-md, subtle tint) so the two badges stack cleanly when both
 * are present on a queue row. Colour mapping keeps the prior semantic
 * cues:
 *   - new          → neutral grey, default state
 *   - reviewing    → amber, in progress
 *   - draft_saved  → sky, work parked
 *   - deferred     → zinc-700, snoozed
 *   - no_response… → muted zinc, terminal "ignore"
 *   - needs_review → red, requires attention
 */
const STYLES: Record<ReviewStatus, { bg: string; text: string; ring: string; dot: string }> = {
  new: {
    bg: 'bg-zinc-900',
    text: 'text-zinc-300',
    ring: 'ring-zinc-800',
    dot: 'bg-zinc-500',
  },
  reviewing: {
    bg: 'bg-amber-950',
    text: 'text-amber-200',
    ring: 'ring-amber-900',
    dot: 'bg-amber-400',
  },
  draft_saved: {
    bg: 'bg-sky-950',
    text: 'text-sky-200',
    ring: 'ring-sky-900',
    dot: 'bg-sky-400',
  },
  deferred: {
    bg: 'bg-zinc-900',
    text: 'text-zinc-400',
    ring: 'ring-zinc-800',
    dot: 'bg-zinc-600',
  },
  no_response_needed: {
    bg: 'bg-zinc-900',
    text: 'text-zinc-500',
    ring: 'ring-zinc-800',
    dot: 'bg-zinc-700',
  },
  needs_review: {
    bg: 'bg-red-950',
    text: 'text-red-300',
    ring: 'ring-red-900',
    dot: 'bg-red-400',
  },
};

const LABELS: Record<ReviewStatus, string> = {
  new: 'New',
  reviewing: 'In review',
  draft_saved: 'Draft',
  deferred: 'Deferred',
  no_response_needed: 'No response',
  needs_review: 'Needs review',
};

interface Props {
  status: ReviewStatus;
  size?: 'sm' | 'xs';
}

export function StatusBadge({ status, size = 'sm' }: Props) {
  const s = STYLES[status];
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1 rounded-md font-medium ring-1 ring-inset ${s.bg} ${s.text} ${s.ring} ${
        size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-1 py-px text-[9px]'
      }`}
    >
      <span aria-hidden="true" className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {LABELS[status]}
    </span>
  );
}
